import { FunctionComponent, useEffect, useRef } from 'react';
import { Box, Text, Link as ChakraLink, Heading } from '@chakra-ui/react';
import { Link as ReactRouterLink } from 'react-router-dom';
import { FormProvider, StepperProvider } from 'src/context';
import {
  AdditionalFacilitiesForm,
  EmployerStatementForm,
  MortgageCertificateForm,
  InterestProvidersList,
  QuestionnaireList,
  DecisionTree,
  CalculatorStudentLoan,
  CalculatorsBuyerCosts,
  CalculatorsHomeValue,
  PrivacyForm,
  SustainabilityScanForm,
  BikeRelay40Years,
  CalculatorSquareMeterAverage,
  InterestRatesHistoricalSlider,
  CalculatorAdditionalPayment
} from 'src/components/features';
import { getQueryParam, mainMediaMobile } from 'src/utils';
import useMediaQuery from '../../../../hooks/use-media';

import { InterestProvidersProvider } from '../../../features/interest-providers-list/context';
import { DecisionTreeProvider } from '../../../features/decision-tree/context/decision-tree-context';
import { BikeRelay40YearsProvider } from '../../../features/bike-relay-40-years/context/bike-relay-40-years-context';

type ComponentType =
  | 'questionnaire'
  | 'interest-providers-list'
  | 'interest-rates-historical-slider'
  | 'employer-statement-form'
  | 'mortgage-certificate-form'
  | 'additional-facilities-form'
  | 'sustainability-scan-form'
  | 'form-privacy'
  | 'calculator-student-loan'
  | 'calculator-additional-payment'
  | 'calculator-square-meter-average-price'
  | 'decision-tree'
  | 'calculator-home-value'
  | 'calculator-buyer-costs'
  | 'bike-relay-40-years';

export const DynamicRendering: FunctionComponent<{
  component?: ComponentType;
  id?: string;
  rootId: string;
}> = ({ component, rootId, id }) => {
  let timeOutId: ReturnType<typeof setTimeout> | null = null;

  const componentRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery(mainMediaMobile);
  const rootElement = document.getElementById(rootId);
  const dataComponentInit = rootElement?.getAttribute('data-component-init');
  if (dataComponentInit) {
    component = dataComponentInit as ComponentType;
  }
  const toggleTalkJsLauncher = (state: 'hide' | 'show') => {
    const talkJsLauncher = document.querySelector<HTMLDivElement>('.__talkjs_launcher');
    const talkJsPopup = document.querySelector<HTMLDivElement>('.__talkjs_popup');
    if (!talkJsLauncher || !talkJsPopup || !isMobile) return;
    const reopenTalkJsPopup = talkJsLauncher.classList.contains('open');
    const displayState = state === 'hide' ? 'none' : 'block';
    talkJsLauncher.style.display = displayState;
    if (reopenTalkJsPopup) {
      talkJsPopup.style.display = displayState;
    }
  };

  const toggleCtaButton = (state: 'hide' | 'show') => {
    const ctaButton = document.querySelector<HTMLDivElement>('.c-cta-sticky');
    if (!ctaButton || !isMobile) return;
    ctaButton.style.display = state === 'hide' ? 'none' : 'block';
  };

  const getId = () => {
    if (id) {
      return id;
    }

    if (getQueryParam('id')) {
      return getQueryParam('id');
    }

    if (rootElement?.getAttribute('data-id')) {
      return rootElement?.getAttribute('data-id') as string;
    }

    return '';
  };

  const toggleElements = (state: 'hide' | 'show') => {
    toggleTalkJsLauncher(state);
    toggleCtaButton(state);
  };
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (timeOutId) clearTimeout(timeOutId);
          toggleElements('hide');
          //console.log(`${component} is in view`); // Perform your action here
        } else {
          timeOutId = setTimeout(() => {
            toggleElements('show');
          }, 1000);
          //console.log(`${component} is in out view`); // Perform your action here
        }
      },
      { threshold: 0.3 } // Adjust the threshold as needed
    );

    if (componentRef.current) {
      observer.observe(componentRef.current);
    }

    return () => {
      if (componentRef.current) {
        observer.unobserve(componentRef.current);
      }
    };
  }, []);

  const renderComponent = () => {
    switch (component) {
      case 'bike-relay-40-years':
        return (
          <BikeRelay40YearsProvider>
            <BikeRelay40Years></BikeRelay40Years>
          </BikeRelay40YearsProvider>
        );

      case 'calculator-additional-payment':
        return (
          <FormProvider>
            <StepperProvider>
              <CalculatorAdditionalPayment />
            </StepperProvider>
          </FormProvider>
        );
      case 'calculator-home-value':
        return (
          <FormProvider>
            <StepperProvider>
              <CalculatorsHomeValue />
            </StepperProvider>
          </FormProvider>
        );

      case 'calculator-buyer-costs':
        return (
          <FormProvider>
            <StepperProvider>
              <CalculatorsBuyerCosts />
            </StepperProvider>
          </FormProvider>
        );

      case 'calculator-student-loan':
        return (
          <FormProvider>
            <StepperProvider>
              <CalculatorStudentLoan />
            </StepperProvider>
          </FormProvider>
        );

      case 'calculator-square-meter-average-price':
        return (
          <FormProvider>
            <CalculatorSquareMeterAverage />
          </FormProvider>
        );

      case 'questionnaire':
        return (
          <FormProvider>
            <QuestionnaireList id={getId()} />
          </FormProvider>
        );

      case 'interest-providers-list':
        return (
          <InterestProvidersProvider>
            <InterestProvidersList id={getId()} />
          </InterestProvidersProvider>
        );

      case 'employer-statement-form':
        return (
          <FormProvider>
            <EmployerStatementForm />
          </FormProvider>
        );

      case 'additional-facilities-form':
        return (
          <FormProvider>
            <AdditionalFacilitiesForm />
          </FormProvider>
        );

      case 'sustainability-scan-form':
        return (
          <FormProvider>
            <SustainabilityScanForm />
          </FormProvider>
        );

      case 'mortgage-certificate-form':
        return (
          <FormProvider>
            <MortgageCertificateForm />
          </FormProvider>
        );

      case 'form-privacy':
        return (
          <FormProvider>
            <PrivacyForm />
          </FormProvider>
        );

      case 'decision-tree':
        return (
          <DecisionTreeProvider type={getId()}>
            <DecisionTree />
          </DecisionTreeProvider>
        );

      case 'interest-rates-historical-slider':
        return <InterestRatesHistoricalSlider title="Historische rentestanden" />;

      default:
        return (
          <Box p={4} className={`list-none`}>
            <Heading as={'h1'}>{`Kies een tool`}</Heading>
            <ul>
              <li>
                <Text>
                  <ChakraLink as={ReactRouterLink} to="/react-tool-bike-relay-40-years.html">
                    bike-relay-40-years
                  </ChakraLink>
                </Text>
              </li>
              <li>
                <Text>
                  <ChakraLink as={ReactRouterLink} to="/react-tool-decision-tree-divorce.html">
                    decision-tree-divorce
                  </ChakraLink>
                </Text>
              </li>
              <li>
                <Text>
                  <ChakraLink as={ReactRouterLink} to="/react-tool-decision-tree-remodel.html">
                    decision-tree-remodel
                  </ChakraLink>
                </Text>
              </li>
              <li>
                <Text>
                  <ChakraLink as={ReactRouterLink} to="/react-tool-calculator-home-value.html">
                    calculator-home-value
                  </ChakraLink>
                </Text>
              </li>
              <li>
                <Text>
                  <ChakraLink
                    as={ReactRouterLink}
                    to="/react-tool-calculator-additional-payment.html">
                    calculator-additional-payment
                  </ChakraLink>
                </Text>
              </li>
              <li>
                <Text>
                  <ChakraLink as={ReactRouterLink} to="/react-tool-calculator-buyer-costs.html">
                    calculator-buyer-costs
                  </ChakraLink>
                </Text>
              </li>
              <li>
                <Text>
                  <ChakraLink as={ReactRouterLink} to="/react-tool-calculator-student-loan.html">
                    calculator-student-loan
                  </ChakraLink>
                </Text>
              </li>
              <li>
                <Text>
                  <ChakraLink
                    as={ReactRouterLink}
                    to="/react-tool-calculator-square-meter-average-price.html">
                    calculator-square-meter-average-price
                  </ChakraLink>
                </Text>
              </li>
              <li>
                <Text>
                  <ChakraLink as={ReactRouterLink} to="/react-tool-interest-providers-list.html">
                    interest-providers-list
                  </ChakraLink>
                </Text>
              </li>
              <li>
                <Text>
                  <ChakraLink
                    as={ReactRouterLink}
                    to="/react-tool-interest-rates-historical-slider.html">
                    interest-rates-historical-slider
                  </ChakraLink>
                </Text>
              </li>
              <li>
                <Text>
                  <ChakraLink as={ReactRouterLink} to="/react-tool-form-sustainability-scan.html">
                    form-sustainability-scan
                  </ChakraLink>
                </Text>
              </li>
              <li>
                <Text>
                  <ChakraLink as={ReactRouterLink} to="/react-tool-form-employer-statement.html">
                    form-employer-statement
                  </ChakraLink>
                </Text>
              </li>
              <li>
                <Text>
                  <ChakraLink as={ReactRouterLink} to="/react-tool-form-additional-facilities.html">
                    form-additional-facilities
                  </ChakraLink>
                </Text>
              </li>
              <li>
                <Text>
                  <ChakraLink as={ReactRouterLink} to="/react-tool-form-mortgage-certificate.html">
                    form-mortgage-certificate
                  </ChakraLink>
                </Text>
              </li>
              <li>
                <Text>
                  <ChakraLink as={ReactRouterLink} to="/react-tool-form-privacy.html">
                    form-privacy-formulier
                  </ChakraLink>
                </Text>
              </li>
              <li>
                <Text>
                  <ChakraLink as={ReactRouterLink} to="/react-tool-questionnaire.html">
                    questionnaire
                  </ChakraLink>
                </Text>
              </li>
            </ul>
          </Box>
        );
    }
  };

  return (
    <div ref={componentRef} className={`min-h-[500px]`}>
      {renderComponent()}
    </div>
  );
};
